import { deleteField, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import type { AppUser } from '../types';
import { db } from '../backend/firebase';

const SKIP_CACHE_PREFIX = 'yetrykar-profile-prompt-skipped:';
const PROMPT_INTERVAL_MS = 30 * 24 * 60 * 60 * 1000;

const cacheKey = (uid: string) => `${SKIP_CACHE_PREFIX}${uid}`;

const toMillis = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'object' && value !== null) {
    if ('toMillis' in value && typeof value.toMillis === 'function') {
      const parsed = value.toMillis();
      return Number.isFinite(parsed) ? parsed : null;
    }
    if ('seconds' in value && typeof value.seconds === 'number') {
      return value.seconds * 1000;
    }
  }
  return null;
};

const readCachedSkip = (uid: string): number | null => {
  try {
    const value = localStorage.getItem(cacheKey(uid));
    return value ? toMillis(value) : null;
  } catch (error) {
    console.warn('Could not read the profile prompt cache:', error);
    return null;
  }
};

const cacheSkip = (uid: string, timestamp: number): void => {
  try {
    localStorage.setItem(cacheKey(uid), String(timestamp));
  } catch (error) {
    console.warn('Could not update the profile prompt cache:', error);
  }
};

export const markProfilePromptSkipped = async (uid: string): Promise<void> => {
  if (!uid) throw new Error('A signed-in user is required to skip the profile prompt.');
  await updateDoc(doc(db, 'users', uid), {
    profilePromptSkippedAt: serverTimestamp()
  });
  cacheSkip(uid, Date.now());
};

export const clearProfilePromptSkip = async (uid: string): Promise<void> => {
  if (!uid) throw new Error('A signed-in user is required to clear the profile prompt skip.');
  await updateDoc(doc(db, 'users', uid), {
    profilePromptSkippedAt: deleteField()
  });
  try {
    localStorage.removeItem(cacheKey(uid));
  } catch (error) {
    console.warn('Could not clear the profile prompt cache:', error);
  }
};

export const shouldShowProfilePrompt = (
  user: Pick<AppUser, 'uid' | 'profileComplete' | 'profilePromptSkippedAt'>
): boolean => {
  return false; // Disabled per user request
};
